import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ListChecks, Save, Plus, Trash2, GripVertical, Eye, ChevronUp, ChevronDown, AlertTriangle } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TYPE_LABELS = {
  single_choice: "Single choice",
  multi_choice: "Multi-select",
  yes_no: "Yes / No",
  numeric_scale: "Numeric scale",
};

function Field({ label, children, hint, required }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">
        {label}{required && <span className="text-red-600"> *</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-[#9CA3AF]">{hint}</div>}
    </div>
  );
}

function newOption() {
  return { id: `tmp-${Math.random().toString(36).slice(2, 9)}`, label_en: "", label_ar: "", score: 0 };
}

function emptyQuestionForType(type) {
  const base = { text_en: "", text_ar: "", type, active: true, options: [], scale: null };
  if (type === "yes_no") {
    base.options = [
      { ...newOption(), label_en: "Yes", label_ar: "نعم", score: 1 },
      { ...newOption(), label_en: "No", label_ar: "لا", score: 0 },
    ];
  } else if (type === "single_choice" || type === "multi_choice") {
    base.options = [newOption(), newOption()];
  } else if (type === "numeric_scale") {
    base.scale = { min: 0, max: 10, score_multiplier: 1 };
  }
  return base;
}

function QuestionEditor({ open, initial, onClose, onSaved }) {
  const [q, setQ] = useState(initial || emptyQuestionForType("single_choice"));
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setQ(initial || emptyQuestionForType("single_choice")); }, [open, initial]);

  function setType(t) {
    if (t === q.type) return;
    // If switching to numeric_scale, drop options; else keep options if valid
    if (t === "numeric_scale") {
      setQ({ ...q, type: t, options: [], scale: q.scale || { min: 0, max: 10, score_multiplier: 1 } });
    } else if (t === "yes_no") {
      setQ({ ...q, type: t, scale: null, options: [
        { ...newOption(), label_en: "Yes", label_ar: "نعم", score: 1 },
        { ...newOption(), label_en: "No", label_ar: "لا", score: 0 },
      ]});
    } else {
      setQ({ ...q, type: t, scale: null, options: q.options?.length ? q.options : [newOption(), newOption()] });
    }
  }

  function updateOption(i, patch) {
    setQ({ ...q, options: q.options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)) });
  }
  function addOption() { setQ({ ...q, options: [...q.options, newOption()] }); }
  function removeOption(i) {
    if (q.type === "yes_no") return;
    setQ({ ...q, options: q.options.filter((_, idx) => idx !== i) });
  }
  function updateScale(patch) {
    setQ({ ...q, scale: { ...(q.scale || {}), ...patch } });
  }

  function validate() {
    if (!q.text_en?.trim()) return "English question text is required.";
    if (!q.text_ar?.trim()) return "Arabic question text is required.";
    if (q.type === "numeric_scale") {
      const s = q.scale || {};
      if (s.max <= s.min) return "Scale max must be greater than min.";
      if (s.score_multiplier < 0) return "Score multiplier cannot be negative.";
    } else {
      if (!q.options?.length) return "At least one option is required.";
      for (const o of q.options) {
        if (!o.label_en?.trim() || !o.label_ar?.trim()) return "Every option needs both EN and AR labels.";
      }
      if (q.type === "yes_no" && q.options.length !== 2) return "Yes/No must have exactly 2 options.";
    }
    return null;
  }

  async function save() {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        text_en: q.text_en.trim(),
        text_ar: q.text_ar.trim(),
        type: q.type,
        active: !!q.active,
        options: q.type === "numeric_scale" ? [] : q.options.map((o) => ({
          id: (o.id || "").startsWith("tmp-") ? undefined : o.id,
          label_en: o.label_en.trim(),
          label_ar: o.label_ar.trim(),
          score: Number(o.score) || 0,
        })),
        scale: q.type === "numeric_scale" ? {
          min: Number(q.scale.min) || 0,
          max: Number(q.scale.max) || 10,
          score_multiplier: Number(q.scale.score_multiplier) || 0,
        } : null,
      };
      if (q.id) {
        await api.put(`/callbacks/questionnaire/${q.id}`, payload);
        toast.success("Question updated");
      } else {
        await api.post("/callbacks/questionnaire", payload);
        toast.success("Question added");
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="question-editor-dialog">
        <DialogHeader>
          <DialogTitle>{q.id ? "Edit question" : "Add question"}</DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">
            Bilingual (EN + AR) text is required. Yes/No labels are fixed — edit scores only.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Question (English)" required>
              <Textarea rows={2} value={q.text_en} onChange={(e) => setQ({ ...q, text_en: e.target.value })}
                placeholder="e.g. How would you rate your current pain?"
                data-testid="question-text-en" />
            </Field>
            <Field label="Question (Arabic)" required>
              <Textarea rows={2} value={q.text_ar} onChange={(e) => setQ({ ...q, text_ar: e.target.value })}
                dir="rtl" placeholder="مثال: كيف تصف الألم الآن؟"
                data-testid="question-text-ar" />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Question type" required>
              <Select value={q.type} onValueChange={setType}>
                <SelectTrigger data-testid="question-type-select"><SelectValue>{TYPE_LABELS[q.type]}</SelectValue></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([v, l]) => (
                    <SelectItem key={v} value={v} data-testid={`question-type-${v}`}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Active" hint="Inactive questions are hidden from the mobile app.">
              <div className="flex items-center gap-2 h-9">
                <Switch checked={!!q.active} onCheckedChange={(v) => setQ({ ...q, active: v })}
                  data-testid="question-active-switch" />
                <span className="text-sm text-[#374151]">{q.active ? "Visible to patients" : "Hidden"}</span>
              </div>
            </Field>
          </div>

          {q.type === "numeric_scale" ? (
            <div className="border border-[#E5E7EB] rounded-md p-3 space-y-3">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Numeric scale</div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Min">
                  <Input type="number" value={q.scale?.min ?? 0} onChange={(e) => updateScale({ min: Number(e.target.value) })}
                    data-testid="scale-min" />
                </Field>
                <Field label="Max">
                  <Input type="number" value={q.scale?.max ?? 10} onChange={(e) => updateScale({ max: Number(e.target.value) })}
                    data-testid="scale-max" />
                </Field>
                <Field label="Score per point" hint="final = value × multiplier">
                  <Input type="number" step="0.1" value={q.scale?.score_multiplier ?? 1}
                    onChange={(e) => updateScale({ score_multiplier: Number(e.target.value) })}
                    data-testid="scale-multiplier" />
                </Field>
              </div>
            </div>
          ) : (
            <div className="border border-[#E5E7EB] rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Options</div>
                {q.type !== "yes_no" && (
                  <Button type="button" size="sm" onClick={addOption}
                    className="bg-[#0B7E8C] hover:bg-[#128C97] text-white h-7 text-xs" data-testid="add-option-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add option
                  </Button>
                )}
              </div>
              {q.options.map((o, i) => (
                <div key={o.id || i} className="grid grid-cols-12 gap-2 items-end" data-testid={`option-row-${i}`}>
                  <div className="col-span-4">
                    <Field label="Label (EN)">
                      <Input value={o.label_en} onChange={(e) => updateOption(i, { label_en: e.target.value })}
                        disabled={q.type === "yes_no"}
                        data-testid={`option-label-en-${i}`} />
                    </Field>
                  </div>
                  <div className="col-span-4">
                    <Field label="Label (AR)">
                      <Input value={o.label_ar} dir="rtl"
                        onChange={(e) => updateOption(i, { label_ar: e.target.value })}
                        disabled={q.type === "yes_no"}
                        data-testid={`option-label-ar-${i}`} />
                    </Field>
                  </div>
                  <div className="col-span-3">
                    <Field label="Score">
                      <Input type="number" step="0.5" value={o.score}
                        onChange={(e) => updateOption(i, { score: e.target.value })}
                        data-testid={`option-score-${i}`} />
                    </Field>
                  </div>
                  <div className="col-span-1">
                    {q.type !== "yes_no" && (
                      <Button type="button" variant="outline" size="icon"
                        onClick={() => removeOption(i)}
                        className="border-[#E5E7EB] text-red-600 hover:bg-red-50 h-9 w-9"
                        data-testid={`option-remove-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E7EB]" data-testid="question-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving}
            className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="question-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ThresholdCard({ threshold, onSave }) {
  const [val, setVal] = useState(threshold);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(threshold); }, [threshold]);
  async function save() {
    setSaving(true);
    try {
      await api.put("/callbacks/questionnaire/thresholds", { stat_threshold: Number(val) });
      toast.success("Urgency threshold saved");
      onSave();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }
  return (
    <div className="hah-card p-4 flex items-end gap-4" data-testid="threshold-card">
      <div className="flex-1">
        <Field label="STAT threshold" hint="Total score at or above this value flags the callback as urgent (STAT). Anything lower is Normal.">
          <Input type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)}
            className="max-w-[160px]" data-testid="threshold-input" />
        </Field>
      </div>
      <Button onClick={save} disabled={saving}
        className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="threshold-save-btn">
        <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save threshold"}
      </Button>
    </div>
  );
}

function PreviewCard({ questions, threshold }) {
  const [answers, setAnswers] = useState({}); // {qid: {selected_option_ids, numeric_value}}
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  function setAns(qid, patch) {
    setAnswers({ ...answers, [qid]: { ...(answers[qid] || {}), ...patch } });
  }

  async function run() {
    setRunning(true);
    try {
      const payload = {
        answers: questions.map((q) => ({
          question_id: q.id,
          selected_option_ids: answers[q.id]?.selected_option_ids || [],
          numeric_value: answers[q.id]?.numeric_value ?? null,
        })).filter((a) => a.selected_option_ids.length || a.numeric_value != null),
      };
      const r = await api.post("/callbacks/score", payload);
      setResult(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  }

  if (!questions.length) return null;
  return (
    <div className="hah-card p-4 space-y-3" data-testid="preview-card">
      <div className="flex items-center gap-2">
        <Eye className="w-4 h-4 text-[#0B7E8C]" />
        <h3 className="text-base font-semibold text-[#1F2937]">Preview — patient flow</h3>
      </div>
      <p className="text-xs text-[#6B7280]">
        Answer as a patient would, then click "Compute urgency" to see how the current scoring rules classify the request.
        STAT threshold: <b>{threshold}</b>.
      </p>
      <div className="space-y-3 border-t border-[#E5E7EB] pt-3">
        {questions.filter((q) => q.active).map((q) => {
          const a = answers[q.id] || {};
          return (
            <div key={q.id} className="space-y-1" data-testid={`preview-q-${q.id}`}>
              <div className="text-sm font-medium text-[#1F2937]">{q.text_en}</div>
              {q.type === "numeric_scale" ? (
                <Input type="number" min={q.scale?.min} max={q.scale?.max}
                  value={a.numeric_value ?? ""}
                  onChange={(e) => setAns(q.id, { numeric_value: Number(e.target.value) })}
                  className="max-w-[140px]" data-testid={`preview-numeric-${q.id}`} />
              ) : q.type === "multi_choice" ? (
                <div className="flex flex-wrap gap-2">
                  {(q.options || []).map((o) => {
                    const sel = (a.selected_option_ids || []).includes(o.id);
                    return (
                      <button key={o.id} type="button"
                        onClick={() => {
                          const cur = a.selected_option_ids || [];
                          setAns(q.id, { selected_option_ids: sel ? cur.filter((x) => x !== o.id) : [...cur, o.id] });
                        }}
                        className={`px-3 py-1.5 rounded-md text-xs border ${sel ? "bg-[#0B7E8C] text-white border-[#0B7E8C]" : "bg-white text-[#374151] border-[#E5E7EB]"}`}
                        data-testid={`preview-option-${q.id}-${o.id}`}>
                        {o.label_en} <span className="opacity-70">(+{o.score})</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(q.options || []).map((o) => {
                    const sel = (a.selected_option_ids || [])[0] === o.id;
                    return (
                      <button key={o.id} type="button"
                        onClick={() => setAns(q.id, { selected_option_ids: [o.id] })}
                        className={`px-3 py-1.5 rounded-md text-xs border ${sel ? "bg-[#0B7E8C] text-white border-[#0B7E8C]" : "bg-white text-[#374151] border-[#E5E7EB]"}`}
                        data-testid={`preview-option-${q.id}-${o.id}`}>
                        {o.label_en} <span className="opacity-70">(+{o.score})</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3">
        <Button onClick={run} disabled={running}
          className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="preview-run-btn">
          {running ? "Computing…" : "Compute urgency"}
        </Button>
        {result && (
          <div className="text-sm" data-testid="preview-result">
            Score: <b>{result.total_score}</b> → <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${result.urgency === "urgent" ? "bg-red-50 text-red-700 border border-red-200" : "bg-slate-50 text-slate-700 border border-slate-200"}`}>
              {result.urgency === "urgent" && <AlertTriangle className="w-3 h-3" />}
              {result.urgency_label}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CallbackQuestionnaire() {
  const [items, setItems] = useState(null);
  const [threshold, setThreshold] = useState(5);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/callbacks/questionnaire");
      setItems(r.data.items || []);
      setThreshold(r.data.thresholds?.stat_threshold ?? 5);
    } catch (e) { toast.error(formatApiError(e)); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function move(i, dir) {
    const j = i + dir;
    if (!items || j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    setBusy(true);
    try {
      await api.post("/callbacks/questionnaire/reorder", { ordered_ids: next.map((q) => q.id) });
    } catch (e) { toast.error(formatApiError(e)); load(); }
    finally { setBusy(false); }
  }

  async function remove(q) {
    if (!window.confirm(`Delete question "${q.text_en}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/callbacks/questionnaire/${q.id}`);
      toast.success("Question deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function toggleActive(q) {
    setBusy(true);
    try {
      await api.put(`/callbacks/questionnaire/${q.id}`, {
        text_en: q.text_en, text_ar: q.text_ar, type: q.type,
        active: !q.active, options: q.options || [], scale: q.scale || null,
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  const questions = useMemo(() => items || [], [items]);

  return (
    <div data-testid="callback-questionnaire-page">
      <PageHeader
        title="Callback Urgency Questionnaire"
        subtitle="Configure the scored questions the mobile app presents to patients when they request a call-back. The summed score decides whether the request is Normal or STAT (urgent)."
        icon={ListChecks}
      />

      {items === null ? (
        <div className="text-sm text-[#6B7280]" data-testid="questionnaire-loading">Loading…</div>
      ) : (
        <div className="space-y-5 max-w-4xl">
          <ThresholdCard threshold={threshold} onSave={load} />

          <div className="hah-card">
            <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB]">
              <h3 className="text-base font-semibold text-[#1F2937]">Questions ({questions.length})</h3>
              <Button size="sm" onClick={() => { setEditing(null); setEditorOpen(true); }}
                className="bg-[#0B7E8C] hover:bg-[#128C97] text-white h-8" data-testid="add-question-btn">
                <Plus className="w-4 h-4 mr-1" /> Add question
              </Button>
            </div>
            {questions.length === 0 ? (
              <div className="p-6 text-sm text-[#6B7280]" data-testid="questionnaire-empty">
                No questions yet. Add one to start scoring patient callback urgency.
              </div>
            ) : (
              <div className="divide-y divide-[#E5E7EB]">
                {questions.map((q, i) => {
                  const maxScore = q.type === "numeric_scale"
                    ? Number((q.scale?.max || 0) * (q.scale?.score_multiplier || 0)).toFixed(1)
                    : q.type === "multi_choice"
                      ? (q.options || []).reduce((s, o) => s + Number(o.score || 0), 0)
                      : Math.max(0, ...((q.options || []).map((o) => Number(o.score || 0))));
                  return (
                    <div key={q.id} className="p-4 flex items-start gap-3" data-testid={`question-row-${q.id}`}>
                      <div className="flex flex-col gap-1 pt-1">
                        <button className="text-[#6B7280] hover:text-[#0B7E8C] disabled:opacity-30"
                          onClick={() => move(i, -1)} disabled={i === 0 || busy}
                          data-testid={`question-up-${q.id}`}><ChevronUp className="w-4 h-4" /></button>
                        <GripVertical className="w-4 h-4 text-[#D1D5DB]" />
                        <button className="text-[#6B7280] hover:text-[#0B7E8C] disabled:opacity-30"
                          onClick={() => move(i, 1)} disabled={i === questions.length - 1 || busy}
                          data-testid={`question-down-${q.id}`}><ChevronDown className="w-4 h-4" /></button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-[#0B7E8C] bg-[#E6F5F7] px-2 py-0.5 rounded">
                            {TYPE_LABELS[q.type] || q.type}
                          </span>
                          {!q.active && (
                            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280] bg-slate-100 px-2 py-0.5 rounded">Inactive</span>
                          )}
                          <span className="text-[11px] text-[#6B7280]">Max score contribution: <b>{maxScore}</b></span>
                        </div>
                        <div className="mt-1 text-sm font-medium text-[#1F2937]" data-testid={`question-text-en-${q.id}`}>{q.text_en}</div>
                        <div className="text-sm text-[#6B7280]" dir="rtl" data-testid={`question-text-ar-${q.id}`}>{q.text_ar}</div>
                        {q.type !== "numeric_scale" && (q.options || []).length > 0 && (
                          <ul className="mt-2 text-xs text-[#374151] space-y-0.5">
                            {q.options.map((o) => (
                              <li key={o.id} className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-[#0B7E8C] rounded-full inline-block" />
                                <span>{o.label_en} <span className="opacity-70">/ {o.label_ar}</span></span>
                                <span className="ml-auto text-[#6B7280]">score +{Number(o.score || 0)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {q.type === "numeric_scale" && q.scale && (
                          <div className="mt-1 text-xs text-[#6B7280]">
                            Scale {q.scale.min}–{q.scale.max}, multiplier ×{q.scale.score_multiplier}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <Switch checked={!!q.active} onCheckedChange={() => toggleActive(q)}
                          disabled={busy} data-testid={`question-active-toggle-${q.id}`} />
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => { setEditing(q); setEditorOpen(true); }}
                            className="border-[#E5E7EB] h-7 text-xs" data-testid={`question-edit-${q.id}`}>Edit</Button>
                          <Button size="sm" variant="outline" onClick={() => remove(q)}
                            className="border-[#E5E7EB] text-red-600 hover:bg-red-50 h-7 text-xs"
                            data-testid={`question-delete-${q.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <PreviewCard questions={questions} threshold={threshold} />
        </div>
      )}

      <QuestionEditor
        open={editorOpen}
        initial={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
